import { useParams, Link } from "react-router-dom";
import SeoHead from "@/components/feature/SeoHead";
import Breadcrumb from "@/components/feature/Breadcrumb";
import { articleDetails, featuredArticles } from "@/mocks/articles";
import { categories } from "@/mocks/categories";

export default function ArticlePage() {
  const { slug } = useParams<{ slug: string }>();
  const article = slug ? articleDetails[slug] : null;

  if (!article) {
    return (
      <div className="pt-20">
        <div className="max-w-7xl mx-auto px-4 md:px-6 py-20 text-center">
          <div className="w-16 h-16 mx-auto flex items-center justify-center rounded-full bg-background-100 text-foreground-400 mb-4">
            <i className="ri-file-unknow-line text-2xl"></i>
          </div>
          <h1 className="text-xl font-heading font-bold text-foreground-900 mb-2">文章未找到</h1>
          <p className="text-foreground-500 text-sm mb-6">抱歉，您查看的文章可能已被移除或地址有误。</p>
          <Link to="/" className="inline-flex items-center gap-2 px-5 py-2.5 bg-primary-500 text-background-50 rounded-lg text-sm font-medium hover:bg-primary-600 transition-colors cursor-pointer">
            <i className="ri-home-4-line"></i>
            返回首页
          </Link>
        </div>
      </div>
    );
  }

  const relatedArticles = featuredArticles
    .filter((a) => a.slug !== slug)
    .slice(0, 3);

  const jsonLd = {
    "@context": "https://schema.org",
    "@type": "Article",
    headline: article.title,
    description: article.content[0]?.type === "p" ? article.content[0].text?.substring(0, 160) : article.title,
    datePublished: article.date,
    dateModified: article.date,
    inLanguage: "zh-CN",
    articleSection: article.category,
    publisher: {
      "@type": "Organization",
      name: "禁漫天堂",
    },
  };

  return (
    <>
      <SeoHead
        title={`${article.title} – 禁漫天堂`}
        description={article.content[0]?.type === "p" ? (article.content[0].text?.substring(0, 160) || article.title) : article.title}
        keywords={`禁漫天堂${article.category},${article.title.substring(0, 30)}`}
        canonicalPath={`/article/${slug}`}
        ogType="article"
        jsonLd={jsonLd}
      />

      <div className="pt-20">
        <div className="max-w-4xl mx-auto px-4 md:px-6 py-10">
          <Breadcrumb
            items={[
              { label: "首页", path: "/" },
              { label: article.category, path: "/categories" },
              { label: article.title },
            ]}
          />

          <article className="mt-8">
            <header className="mb-10">
              <div className="flex items-center gap-3 mb-4">
                <span className="px-3 py-1 bg-accent-100 text-accent-700 rounded-full text-xs font-medium">
                  {article.category}
                </span>
                <span className="text-foreground-400 text-xs flex items-center gap-1">
                  <i className="ri-calendar-line"></i>
                  {article.date}
                </span>
              </div>
              <h1 className="text-2xl md:text-3xl lg:text-4xl font-heading font-bold text-foreground-900 leading-tight">
                {article.title}
              </h1>
            </header>

            <div className="prose-custom">
              {article.content.map((block, index) => {
                if (block.type === "p" && block.text) {
                  return (
                    <p key={index} className="text-foreground-700 text-sm md:text-base leading-relaxed mb-5">
                      {block.text}
                    </p>
                  );
                }
                if (block.type === "h2" && block.text) {
                  return (
                    <h2 key={index} className="text-xl md:text-2xl font-heading font-bold text-foreground-900 mt-10 mb-4">
                      {block.text}
                    </h2>
                  );
                }
                if (block.type === "h3" && block.text) {
                  return (
                    <h3 key={index} className="text-lg font-heading font-semibold text-foreground-800 mt-8 mb-3">
                      {block.text}
                    </h3>
                  );
                }
                if (block.type === "list" && block.items) {
                  return (
                    <ul key={index} className="space-y-2.5 mb-5 pl-5">
                      {block.items.map((item, i) => (
                        <li key={i} className="flex items-start gap-2 text-foreground-700 text-sm md:text-base leading-relaxed">
                          <span className="w-1.5 h-1.5 rounded-full bg-accent-500 mt-2 shrink-0"></span>
                          {item}
                        </li>
                      ))}
                    </ul>
                  );
                }
                if (block.type === "image" && block.imageUrl) {
                  return (
                    <figure key={index} className="my-8">
                      <div className="w-full h-56 md:h-72 rounded-xl overflow-hidden bg-background-100">
                        <img
                          src={block.imageUrl}
                          alt={block.caption || "示意图"}
                          className="w-full h-full object-cover"
                        />
                      </div>
                      {block.caption && (
                        <figcaption className="text-center text-foreground-400 text-xs mt-2">
                          {block.caption}
                        </figcaption>
                      )}
                    </figure>
                  );
                }
                return null;
              })}
            </div>
          </article>

          <div className="mt-16 pt-10 border-t border-background-200/70">
            <h2 className="text-xl font-heading font-bold text-foreground-900 mb-6">
              相关文章推荐
            </h2>
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-5">
              {relatedArticles.map((rel) => (
                <Link
                  key={rel.slug}
                  to={`/article/${rel.slug}`}
                  className="group bg-background-50 border border-background-200/70 rounded-xl p-5 hover:border-accent-300/50 transition-all duration-300 cursor-pointer"
                >
                  <span className="inline-block px-2.5 py-0.5 bg-accent-100 text-accent-700 rounded-md text-xs font-medium mb-3">
                    {rel.category}
                  </span>
                  <h3 className="text-sm font-heading font-semibold text-foreground-900 mb-2 line-clamp-2 group-hover:text-primary-600 transition-colors">
                    {rel.title}
                  </h3>
                  <p className="text-foreground-500 text-xs leading-relaxed line-clamp-2 mb-3">
                    {rel.excerpt}
                  </p>
                  <span className="text-accent-600 text-xs font-medium">
                    阅读全文 →
                  </span>
                </Link>
              ))}
            </div>
          </div>
        </div>
      </div>
    </>
  );
}